import { useEffect, useMemo, useRef, useState } from 'react';
import { COARSE, useMediaQuery } from '../shared/media';
import { FieldError } from './fieldError';
import { STRINGS as S } from './strings';
import { UploadLimits, acceptRestricted, checkFiles, formatBytes, isImageFile } from './uploadLimits';

/** Extensions that mean a camera would be a sensible way to answer. */
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif'];

/**
 * The control behind a 'file' field: a drop zone that is also a button, the
 * list of what is attached (with a thumbnail when it is a picture), and the
 * limits checked the moment a file arrives rather than after Submit.
 *
 * The real `<input type="file">` stays in the document, visually hidden but
 * focusable: Tab reaches it, Enter and Space open the picker natively, and
 * the U1 attributes (aria-invalid, aria-describedby, data-error-path) sit on
 * it so the summary's "focus this field" and the first-error focus both land
 * where the keyboard already is. The drop zone is the click target and wears
 * the focus ring on the input's behalf.
 *
 * A refused file never enters `files`; its message goes up through `onError`
 * and lands under the control the same way a required-field error does.
 */
export interface FileInputProps {
  domId: string;
  files: File[];
  onFiles: (files: File[]) => void;
  limits: UploadLimits;
  disabled?: boolean;
  required?: boolean;
  /** the error under the control (a refusal, or "required") */
  error?: string;
  /** the errors-map key, stamped on the input for the summary */
  errorPath: string;
  /** true when the field has help text (`${domId}-info`) to describe the control with */
  hasInfo?: boolean;
  onClearError?: () => void;
  onError?: (message: string) => void;
}

const sameFile = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

export default function FileInput({
  domId, files, onFiles, limits, disabled, required, error, errorPath, hasInfo, onClearError, onError,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  // A finger cannot drag a file onto anything, so on touch the zone stops
  // asking for a drop and offers the two things a phone can actually do
  // (M2/C3). Both inputs feed the same `take`, so the field's state, its
  // limits and the submitted payload are identical whichever is used.
  const coarse = useMediaQuery(COARSE);
  const canPhotograph = limits.accept.some((e) => IMAGE_EXT.includes(e));
  const limitsId = `${domId}-limits`;
  const errId = `${domId}-err`;
  const described = [hasInfo ? `${domId}-info` : '', limitsId, error ? errId : ''].filter(Boolean).join(' ');

  // Thumbnails: an object URL per picture, revoked when the picture goes.
  const thumbs = useObjectUrls(files);

  const take = (incoming: File[]) => {
    if (disabled || !incoming.length) return;
    // A file picked twice is one file.
    const fresh = incoming.filter((f) => !files.some((g) => sameFile(f, g)));
    const check = checkFiles(fresh, limits, files.length);
    if (check.accepted.length) onFiles([...files, ...check.accepted]);
    if (check.error) onError?.(check.error);
    else onClearError?.();
  };

  const remove = (i: number) => {
    onFiles(files.filter((_, j) => j !== i));
    onClearError?.();
    inputRef.current?.focus();
  };

  const open = () => { if (!disabled) inputRef.current?.click(); };
  const openCamera = () => { if (!disabled) cameraRef.current?.click(); };
  const types = acceptRestricted(limits.accept) ? limits.accept.join(', ') : S.files.anyType;
  const zoneClass = [
    'of-file-drop',
    over ? 'is-over' : '',
    error ? 'has-error' : '',
    disabled ? 'is-disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="of-file">
      <div
        className={zoneClass}
        onClick={open}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setOver(true); }}
        onDragEnter={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setOver(false);
          take(Array.from(e.dataTransfer?.files || []));
        }}
      >
        <input
          ref={inputRef}
          id={domId} type="file" className="of-visually-hidden"
          multiple={limits.maxFiles > 1}
          accept={acceptRestricted(limits.accept) ? limits.accept.map((e) => `.${e}`).join(',') : undefined}
          disabled={disabled}
          aria-required={required && !disabled ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={described || undefined}
          data-error-path={error ? errorPath : undefined}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            take(Array.from(e.target.files || []));
            // The same file again should count as a new pick.
            e.target.value = '';
          }}
        />
        {coarse && canPhotograph && (
          <input
            ref={cameraRef} type="file" className="of-visually-hidden" tabIndex={-1}
            accept="image/*" capture="environment"
            disabled={disabled}
            aria-hidden="true"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => { take(Array.from(e.target.files || [])); e.target.value = ''; }}
          />
        )}
        <div className="of-file-drop-text" aria-hidden="true">
          <span className="of-file-drop-icon">⇪</span>
          <span>
            {coarse
              ? (limits.maxFiles > 1 ? S.files.tapMany : S.files.tap)
              : (
                <>
                  {limits.maxFiles > 1 ? S.files.drop : S.files.dropOne}
                  <span className="of-file-browse">{S.files.browse}</span>
                </>
              )}
          </span>
        </div>
        <div className="of-file-limits" id={limitsId}>{S.files.limits(limits.maxFiles, limits.maxSizeMb, types)}</div>
        {coarse && (
          <div className="of-file-actions">
            {canPhotograph && (
              <button
                type="button" className="of-file-act" disabled={disabled}
                onClick={(e) => { e.stopPropagation(); openCamera(); }}
              >{S.files.takePhoto}</button>
            )}
            <button
              type="button" className="of-file-act" disabled={disabled}
              onClick={(e) => { e.stopPropagation(); open(); }}
            >{limits.maxFiles > 1 ? S.files.chooseMany : S.files.choose}</button>
          </div>
        )}
      </div>
      {files.length > 0 && (
        <ul className="of-file-list" aria-label={S.filesSelected(files.length)}>
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}-${i}`} className="of-file-row">
              {thumbs[i]
                ? <img className="of-file-thumb" src={thumbs[i]} alt="" />
                : <span className="of-file-icon" aria-hidden="true">📄</span>}
              <span className="of-file-name" title={f.name}>{f.name}</span>
              <span className="of-file-size">{formatBytes(f.size)}</span>
              {!disabled && (
                <button type="button" className="of-file-remove" onClick={() => remove(i)} aria-label={S.files.remove(f.name)}>×</button>
              )}
            </li>
          ))}
        </ul>
      )}
      <FieldError id={errId} text={error} />
    </div>
  );
}

/** Object URLs for the pictures among `files`, by index; '' for the rest. Revoked as they go. */
function useObjectUrls(files: File[]): string[] {
  const can = typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
  const urls = useMemo(
    () => files.map((f) => (can && isImageFile(f) ? URL.createObjectURL(f) : '')),
    [files, can]
  );
  useEffect(() => () => { for (const u of urls) if (u) URL.revokeObjectURL(u); }, [urls]);
  return urls;
}
